"use client";

import { createContext, type ReactNode, useContext, useEffect, useRef, useState, useSyncExternalStore } from "react";

const never = () => () => {};

const Filled = createContext(false);

/**
 * Fills every frame beneath it at once, wherever it is drawn, the server
 * included. For a reader that needs what a frame holds without a browser to
 * scroll: the tests check each frame's contents this way.
 */
export function FramesFilled({ children }: { children: ReactNode }) {
  return <Filled.Provider value>{children}</Filled.Provider>;
}

/**
 * How far ahead of the viewport a served frame fills: far enough that a
 * reader scrolling towards it finds it drawn.
 */
const AHEAD = "900px";

/**
 * What a product frame holds, drawn in the browser only.
 *
 * A frame served with the page arrives empty, at its full size, and fills
 * once it is within reach of the viewport. So the page's HTML carries no
 * sample workspace: none of the app's links (which lead nowhere on this
 * site), none of its headings, none of its invented records, and a small
 * part of the markup it had (the home's shots were 78% of its elements).
 * The frame's label, caption and summary are outside it and are served as
 * before.
 *
 * A frame drawn by script (a replay's player, which mounts as its shot nears
 * the viewport) fills at once: it is already within reach, and its player
 * expects its contents there.
 *
 * It waits on its parent's box, so the parent must be the sized frame.
 * `ahead` is how far beyond the viewport's top and bottom edges counts as
 * within reach: none at all for a piece that plays as it arrives.
 */
export function FrameContents({ children, ahead = AHEAD }: { children: ReactNode; ahead?: string }) {
  const filled = useContext(Filled);
  const inBrowser = useSyncExternalStore(
    never,
    () => true,
    () => false,
  );
  // Read once, on the first render: a frame that came with the page's HTML renders first on the server's side of this switch.
  const [served] = useState(() => !inBrowser);
  const [near, setNear] = useState(false);
  const marker = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const frame = marker.current?.parentElement;
    if (!frame || near) return;
    if (typeof IntersectionObserver === "undefined") {
      setNear(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setNear(true);
      },
      { rootMargin: `${ahead} 0px` },
    );
    observer.observe(frame);
    return () => observer.disconnect();
  }, [near, ahead]);

  if (filled || (inBrowser && (near || !served))) return children;
  // The frame's own box is the parent; this marks it without taking any room.
  return <span ref={marker} hidden />;
}
